// Pre-formed shape definitions loaded from packages/material-db/data/shapes.yaml.
// A shape is a list of (dx, dy, material) cells stamped relative to a click
// point. The Sandbox API provides paintCircle but not arbitrary stamping;
// stampShape walks the cells and calls paintCircle with radius 0 for each.

import type { SandboxLike } from "./types";
import { parseYaml } from "./yaml";

export interface ShapeCell {
  dx: number;
  dy: number;
  material: string;
  /** Optional state byte. The signal pass reads the low 2 bits as a
   * gate's output direction (0=N, 1=E, 2=S, 3=W). Defaults to 0 if
   * omitted — which matches the legacy "all gates output N" stamp
   * behaviour and stays out of the way for non-gate cells. */
  state?: number;
}

export interface ShapeDef {
  id: string;
  name: string;
  icon: string;
  category?: string;
  cells: ShapeCell[];
  /** Optional dimension-template payload: cells to paint into the
   * shape's child dimension. When present, `stampShape` finds the
   * `dimension` portal cell in `cells`, spawns its child world, and
   * paints these cells into it (offset from the child's centre) — so a
   * sealed sub-circuit drops in fully wired alongside the parent. */
  childCells?: ShapeCell[];
}

interface RawShape {
  id?: unknown;
  name?: unknown;
  icon?: unknown;
  category?: unknown;
  cells?: unknown;
  childCells?: unknown;
}

interface RawCell {
  dx?: unknown;
  dy?: unknown;
  material?: unknown;
  state?: unknown;
}

/** Parse the YAML shape doc into typed shape definitions. */
export function parseShapeDoc(yaml: string): ShapeDef[] {
  const root = parseYaml(yaml) as { shapes?: RawShape[] };
  if (!root || !Array.isArray(root.shapes)) return [];
  const out: ShapeDef[] = [];
  for (const raw of root.shapes) {
    if (!raw || typeof raw !== "object") continue;
    if (typeof raw.id !== "string" || typeof raw.name !== "string") continue;
    if (typeof raw.icon !== "string") continue;
    if (!Array.isArray(raw.cells)) continue;
    const cells: ShapeCell[] = [];
    for (const c of raw.cells as RawCell[]) {
      if (!c || typeof c !== "object") continue;
      if (
        typeof c.dx !== "number" ||
        typeof c.dy !== "number" ||
        typeof c.material !== "string"
      ) {
        continue;
      }
      const cell: ShapeCell = { dx: c.dx, dy: c.dy, material: c.material };
      if (typeof c.state === "number" && Number.isFinite(c.state)) {
        cell.state = c.state & 0xff;
      }
      cells.push(cell);
    }
    if (cells.length === 0) continue;
    // Optional child-dimension cells (same cell shape as `cells`).
    let childCells: ShapeCell[] | undefined;
    if (Array.isArray(raw.childCells)) {
      const cc: ShapeCell[] = [];
      for (const c of raw.childCells as RawCell[]) {
        if (!c || typeof c !== "object") continue;
        if (
          typeof c.dx !== "number" ||
          typeof c.dy !== "number" ||
          typeof c.material !== "string"
        ) {
          continue;
        }
        const cell: ShapeCell = { dx: c.dx, dy: c.dy, material: c.material };
        if (typeof c.state === "number" && Number.isFinite(c.state)) {
          cell.state = c.state & 0xff;
        }
        cc.push(cell);
      }
      if (cc.length > 0) childCells = cc;
    }
    out.push({
      id: raw.id,
      name: raw.name,
      icon: raw.icon,
      category: typeof raw.category === "string" ? raw.category : undefined,
      cells,
      ...(childCells ? { childCells } : {}),
    });
  }
  return out;
}

/** Drop a shape onto the grid centered at `(cx, cy)`. The optional
 * `scale` argument enlarges the stamp uniformly: each cell at
 * `(dx, dy)` is laid down as a `scale x scale` square of cells
 * starting at `(cx + dx*scale, cy + dy*scale)` (rounded to the
 * nearest integer cell). Scale 1 reproduces the original stamp; scale
 * 2 produces a 2x bigger version, etc. Used by `SimCanvas` to grow or
 * shrink the shape while the user drags the pointer before releasing.
 */
export function stampShape(
  sandbox: SandboxLike,
  shape: ShapeDef,
  cx: number,
  cy: number,
  scale: number = 1,
): { placed: number; unknownMaterials: string[] } {
  const w = sandbox.width();
  const h = sandbox.height();
  const unknown = new Set<string>();
  let placed = 0;
  const s = Math.max(1, Math.round(scale));
  for (const cell of shape.cells) {
    const id = sandbox.materialId(cell.material);
    if (id === 0) {
      unknown.add(cell.material);
      continue;
    }
    // Top-left corner of the s*s tile this stamp cell becomes.
    const baseX = cx + cell.dx * s;
    const baseY = cy + cell.dy * s;
    for (let oy = 0; oy < s; oy++) {
      for (let ox = 0; ox < s; ox++) {
        const x = baseX + ox;
        const y = baseY + oy;
        if (x < 0 || y < 0 || x >= w || y >= h) continue;
        // Use paintCellState when the cell carries an explicit state
        // (e.g. an oriented gate), so the engine sees the correct
        // output direction. Fall through to paintCircle otherwise so
        // the cell joins the surrounding rigid body via paint()'s
        // body_id grouping.
        if (cell.state && sandbox.paintCellState) {
          sandbox.paintCellState(x, y, id, cell.state);
        } else {
          sandbox.paintCircle(x, y, 0, id);
        }
        placed++;
      }
    }
  }
  // Dimension-template payload: paint the sealed sub-circuit into the
  // shape's child world so the template drops in fully wired. Find the
  // `dimension` portal cell we just stamped, spawn + enter its child
  // (which switches the viewed layer to the child), paint the child
  // cells, then exit back to the parent. wasm-only — the JS fallback
  // has no dimensions, so the parent footprint just stamps on its own.
  if (
    shape.childCells &&
    sandbox.enterDimension &&
    sandbox.exitDimension &&
    sandbox.paintCellState
  ) {
    const portal = shape.cells.find((c) => c.material === "dimension");
    if (portal) {
      const px = cx + portal.dx * s;
      const py = cy + portal.dy * s;
      if (sandbox.enterDimension(px, py)) {
        for (const cell of shape.childCells) {
          const id = sandbox.materialId(cell.material);
          if (id === 0) {
            unknown.add(cell.material);
            continue;
          }
          const x = CHILD_ANCHOR + cell.dx;
          const y = CHILD_ANCHOR + cell.dy;
          // Child cells are a fixed sub-circuit (never scaled). Always
          // go through paintCellState so oriented gates keep their
          // output direction.
          sandbox.paintCellState(x, y, id, cell.state ?? 0);
          placed++;
        }
        sandbox.exitDimension();
      }
    }
  }
  return { placed, unknownMaterials: [...unknown] };
}

/** Where a dimension template's child sub-circuit is anchored inside
 * the 128×128 child world — offset from the origin so the (small)
 * authored layout sits comfortably away from the edges. Only the
 * relative layout + row-major pin order matter for linking, so the
 * absolute anchor is arbitrary. */
const CHILD_ANCHOR = 40;

/** Bounding box of a shape's stamp cells, scaled by `scale`. Returns
 * the inclusive grid extents — `{ minX, minY, maxX, maxY }` such that
 * the stamp covers cells `(cx + minX, cy + minY)` through
 * `(cx + maxX, cy + maxY)`. Used by the canvas to draw a live
 * preview rectangle while the user is dragging to resize. */
export function shapeBounds(
  shape: ShapeDef,
  scale: number = 1,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const s = Math.max(1, Math.round(scale));
  if (shape.cells.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  let minDx = Infinity;
  let maxDx = -Infinity;
  let minDy = Infinity;
  let maxDy = -Infinity;
  for (const c of shape.cells) {
    if (c.dx < minDx) minDx = c.dx;
    if (c.dx > maxDx) maxDx = c.dx;
    if (c.dy < minDy) minDy = c.dy;
    if (c.dy > maxDy) maxDy = c.dy;
  }
  // Each declared cell at (dx, dy) becomes an s*s tile starting at
  // (dx*s, dy*s); the inclusive extent runs to (dx*s + s - 1).
  return {
    minX: minDx * s,
    minY: minDy * s,
    maxX: maxDx * s + s - 1,
    maxY: maxDy * s + s - 1,
  };
}

/** Group shapes by category for the sidebar. */
export interface ShapeGroup {
  category: string;
  members: ShapeDef[];
}

export function groupShapes(shapes: ShapeDef[]): ShapeGroup[] {
  const buckets = new Map<string, ShapeDef[]>();
  for (const s of shapes) {
    const cat = s.category ?? "other";
    const list = buckets.get(cat) ?? [];
    list.push(s);
    buckets.set(cat, list);
  }
  return [...buckets.entries()].map(([category, members]) => ({ category, members }));
}

/** Fetch a gzipped shape YAML, decompress it (or read it through if
 * the server already did), and parse it with `parseShapeDoc`. Used
 * for very large stamps (millions of cells) that would balloon the
 * JS bundle if inlined as `?raw` text — the `.yaml.gz` is shipped
 * as a static asset and lazy-loaded after the app has mounted, so
 * the initial UI render is unaffected.
 *
 * Robust to either response shape:
 *
 *   - Body is raw gzip bytes (Vite production builds, most static
 *     hosts) — detect the `1f 8b` gzip magic header and pipe the
 *     body through the native `DecompressionStream("gzip")`.
 *   - Body is already-decompressed YAML text (the Vite dev server
 *     advertises `Content-Encoding: gzip` for `.yaml.gz` files, so
 *     the browser auto-decompresses before we see the body) — no
 *     gzip magic header is present; just decode as UTF-8.
 *
 * Returns an empty array on any failure (missing file, decompression
 * error, malformed YAML) so the rest of the shape catalogue stays
 * usable.
 */
export async function loadGzippedShapeDoc(url: string): Promise<ShapeDef[]> {
  try {
    const r = await fetch(url);
    if (!r.ok) return [];
    const bytes = new Uint8Array(await r.arrayBuffer());
    let text: string;
    if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
      if (typeof DecompressionStream === "undefined") return [];
      const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream("gzip"));
      text = await new Response(stream).text();
    } else {
      text = new TextDecoder("utf-8").decode(bytes);
    }
    return parseShapeDoc(text);
  } catch {
    return [];
  }
}
